import React, { FC, useState, useMemo, useEffect } from 'react'
import cronstrue from 'cronstrue'
import { Box, Row, Column, Button, Checkbox, Dialog, Stack, Text, Icon, Tooltip } from 'src/ui'
import { FaEraser, FaExclamationTriangle } from 'react-icons/fa'

interface ResetScheduleSelectionDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (keys: string[]) => void
  config: TranslatorConfigConfig
}

const ResetScheduleSelectionDialog: FC<ResetScheduleSelectionDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  config,
}) => {
  const [selectedSchedules, setSelectedSchedules] = useState<string[]>([])

  // Reset selection when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSchedules([])
    }
  }, [isOpen])

  // Strictly use version property to determine V2
  const isV2 = config.version === '2'

  // V1 Schedules
  const v1Schedules = useMemo(() => {
    if (isV2 || !config.schedules) return []
    // Need to convert V1 map to array of objects to include is_scrubber property easily
    return Object.entries(config.schedules).map(([name, schedule]) => ({
      ...schedule,
      name,
    }))
  }, [config.schedules, isV2])

  // V2 Schedules grouped by adapter
  const v2ScheduleGroups = useMemo(() => {
    if (!isV2 || !config.schedules || !Array.isArray(config.schedules)) return {}

    const groups: { [adapter: string]: IndividualSchedule[] } = {}

    ;(config.schedules as ScheduleGroup[]).forEach((group) => {
      const adapter = group.adapter || 'Unknown'
      if (!groups[adapter]) {
        groups[adapter] = []
      }
      groups[adapter].push(...group.schedules)
    })

    return groups
  }, [config.schedules, isV2])

  // Helper to generate a stable unique key for a schedule
  const getScheduleKey = (
    schedule: IndividualSchedule | Schedule,
    adapter: string,
    index: number,
    isV2: boolean
  ) => {
    if (isV2) {
      const id = (schedule as IndividualSchedule).id
      if (id) return id

      console.error(`A V2 schedule is missing its ID. Adapter: ${adapter}`, schedule)
      // Returning a non-selectable key and disabling the checkbox is a good UI treatment.
      return `INVALID_SCHEDULE_${adapter}_${index}`
    }

    // V1 logic
    const name = (schedule as any).name
    if (name) return name

    // Should not happen for V1, but as a safeguard.
    console.error('A V1 schedule is missing its name!', schedule)
    return `INVALID_SCHEDULE_${adapter}_${index}`
  }

  const allScheduleKeys = useMemo(() => {
    if (isV2) {
      const keys: string[] = []
      Object.entries(v2ScheduleGroups).forEach(([adapter, group]) => {
        group.forEach((s, index) => {
          keys.push(getScheduleKey(s, adapter, index, true))
        })
      })
      return keys.filter((key) => !key.startsWith('INVALID_SCHEDULE'))
    } else {
      // For V1, the 'name' is the key
      return v1Schedules.map((s, index) => getScheduleKey(s, 'v1', index, false))
    }
  }, [isV2, v1Schedules, v2ScheduleGroups])

  const handleConfirm = () => {
    onConfirm(selectedSchedules)
    onClose()
  }

  const toggleSchedule = (key: string) => {
    setSelectedSchedules((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedSchedules([...allScheduleKeys])
    } else {
      setSelectedSchedules([])
    }
  }

  const getScheduleLabel = (schedule: IndividualSchedule | Schedule, index: number) => {
    const mainLabelParts: string[] = []
    const subLabelParts: string[] = []

    if ((schedule as any).name) {
      mainLabelParts.push((schedule as any).name)
    }

    if (schedule.start_date) {
      try {
        const date = new Date(schedule.start_date)
        mainLabelParts.push(`Starts: ${date.toLocaleDateString()}`)
      } catch {
        mainLabelParts.push(`Starts: ${schedule.start_date}`)
      }
    } else if (schedule.start_days_ago) {
      mainLabelParts.push(`Starts: ${schedule.start_days_ago} days ago`)
    }

    if (schedule.cron) {
      try {
        subLabelParts.push(`${cronstrue.toString(schedule.cron)}`)
      } catch {
        subLabelParts.push(`${schedule.cron}`)
      }
    } else if (schedule.interval_minutes) {
      subLabelParts.push(`Every ${schedule.interval_minutes} minutes`)
    }

    // Fallback if no specific info
    if (mainLabelParts.length === 0 && subLabelParts.length === 0) {
      mainLabelParts.push(`Schedule ${index + 1}`)
    }

    return (
      <Column spacing={0}>
        <Text>{mainLabelParts.join(' | ')}</Text>
        {subLabelParts.length > 0 && (
          <Text fontSize="xs" color="gray.500">
            {subLabelParts.join(' | ')}
          </Text>
        )}
      </Column>
    )
  }

  const isAllSelected =
    allScheduleKeys.length > 0 && selectedSchedules.length === allScheduleKeys.length
  const isIndeterminate =
    selectedSchedules.length > 0 && selectedSchedules.length < allScheduleKeys.length

  const actions = (
    <>
      <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
        Cancel
      </Button>

      <Box width={100}>
        <Button
          size="sm"
          colorScheme="primary"
          isDisabled={selectedSchedules.length === 0}
          onClick={handleConfirm}
        >
          Reset
        </Button>
      </Box>
    </>
  )

  return (
    <Dialog title="Reset Schedule(s)" isOpen={isOpen} onClose={onClose} size="md" actions={actions}>
      <Column>
        <Box pb={3} mb={1}>
          <Box>Select one or more schedules to reset.</Box>
        </Box>

        <Row
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #ccc"
          mb={1}
        >
          <Row px={2} alignItems="center">
            <Checkbox
              mb={2}
              isChecked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={toggleSelectAll}
            >
              Select All
            </Checkbox>
          </Row>

          <Box px={2}>
            <Box fontSize="sm">
              {selectedSchedules.length > 0 ? `Selected ${selectedSchedules.length} / ` : null}{' '}
              {allScheduleKeys.length} schedules
            </Box>
          </Box>
        </Row>

        <Box maxHeight={300} overflowY="auto" px={2} borderBottom="1px solid #ccc">
          {isV2 ? (
            <Stack spacing={4} pb={2} pt={2}>
              {Object.entries(v2ScheduleGroups).map(([adapter, schedules]) => (
                <Box key={adapter}>
                  <Text fontWeight="bold" mb={2} fontSize="sm" color="gray.600">
                    {adapter}
                  </Text>
                  <Stack pl={2} spacing={2}>
                    {schedules.map((schedule, index) => {
                      const key = getScheduleKey(schedule, adapter, index, true)
                      const isInvalid = key.startsWith('INVALID_SCHEDULE')
                      const label = getScheduleLabel(schedule, index)

                      return (
                        <Checkbox
                          key={key}
                          isDisabled={isInvalid}
                          isChecked={!isInvalid && selectedSchedules.includes(key)}
                          onChange={() => !isInvalid && toggleSchedule(key)}
                          width="100%"
                          sx={{ '& .chakra-checkbox__label': { width: '100%' } }}
                        >
                          <Row alignItems="center" width="100%" justifyContent="space-between">
                            {label}
                            <Row>
                              {isInvalid && (
                                <Tooltip label="This V2 schedule is missing a required ID and cannot be reset.">
                                  <Icon
                                    as={FaExclamationTriangle as any}
                                    color="red.500"
                                    w={4}
                                    h={4}
                                  />
                                </Tooltip>
                              )}
                              {schedule.is_scrubber && (
                                <Tooltip label="Scrubber Schedule" placement="right">
                                  <Icon
                                    as={FaEraser as any}
                                    ml={2}
                                    color="orange.500"
                                    w={4}
                                    h={4}
                                  />
                                </Tooltip>
                              )}
                            </Row>
                          </Row>
                        </Checkbox>
                      )
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack spacing={2} pb={2} pt={2}>
              {v1Schedules.map((schedule, index) => {
                const key = getScheduleKey(schedule, 'v1', index, false)
                const label = getScheduleLabel(schedule, index)
                return (
                  <Checkbox
                    key={key}
                    isChecked={selectedSchedules.includes(key)}
                    value={key}
                    onChange={() => toggleSchedule(key)}
                    width="100%"
                    sx={{ '& .chakra-checkbox__label': { width: '100%' } }}
                  >
                    <Row alignItems="center" width="100%" justifyContent="space-between">
                      {label}
                      {schedule.is_scrubber && (
                        <Tooltip label="Scrubber Schedule" placement="right">
                          <Icon as={FaEraser as any} ml={2} color="orange.500" w={4} h={4} />
                        </Tooltip>
                      )}
                    </Row>
                  </Checkbox>
                )
              })}
            </Stack>
          )}
        </Box>

        <Row mt={2} fontSize="sm" color="gray.500" alignItems="center">
          <Icon as={FaEraser as any} mr={2} color="orange.500" w={3} h={3} />
          <Text>Indicates a scrubber schedule</Text>
        </Row>
      </Column>
    </Dialog>
  )
}

export default ResetScheduleSelectionDialog
